/**
 * Frido Cross-sell Bundle runtime — "Bundle & Pay Less".
 *
 * Renders the current product (anchor) + pair (add-on) as two selectable cards
 * with a single Buy CTA. On Buy Both:
 *   - the anchor's variant is read LIVE from the PDP's /cart/add form (the Frido
 *     option snippets sync the resolved variant into [name="id"]), never assumed
 *     from render time;
 *   - if the add-on has >1 variant, a compact bottom-sheet opens to pick it —
 *     the anchor is NOT re-asked (already chosen on the PDP);
 *   - a spinner shows on the CTA (or sheet confirm) while /cart/add.js runs.
 * Fallback: if the anchor variant can't be resolved from any form AND the anchor
 * itself has >1 variant, the sheet also shows an anchor selector.
 */
;(() => {
  if (window.__sai_fxsfrd01_initialized__) return
  window.__sai_fxsfrd01_initialized__ = true

  const SNIPPET_ID = 'fxsfrd01'
  const TAG = 'sai-fxsfrd01'
  const FEATURE_SLUG = 'recommendations_fbt'

  function esc(s) {
    return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
  }
  function readPool(node) {
    for (let i = 0; i < node.children.length; i++) {
      const c = node.children[i]
      if (c.tagName === 'SCRIPT' && c.hasAttribute('data-spectrum-snippet-pool')) {
        try { return JSON.parse(c.textContent) } catch (e) { return {} }
      }
    }
    return {}
  }
  function fmtMoney(major, cur) {
    if (major == null || Number.isNaN(Number(major))) return ''
    return (cur || '₹') + Math.round(Number(major)).toLocaleString('en-IN')
  }
  function fmtRating(r) {
    const n = Number(r)
    if (!Number.isFinite(n)) return ''
    return n.toFixed(1)
  }
  function starSvg() {
    return '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l2.9 6.26L21.5 9.3l-4.75 4.4L18 20.5 12 16.9 6 20.5l1.25-6.8L2.5 9.3l6.6-1.04z"/></svg>'
  }
  function checkSvg() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>'
  }
  function plusSvg() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>'
  }

  // Read the anchor product's CURRENTLY-selected variant from the PDP's
  // /cart/add form. Frido option snippets write the resolved variant into
  // [name="id"], so this reflects the live size/colour picker — not render time.
  // Excludes our own snippet (which has no form). Returns a string id or null.
  function liveAnchorVariantId() {
    const forms = document.querySelectorAll('form[action*="/cart/add"]')
    for (const f of forms) {
      if (f.closest(TAG)) continue
      const inp = f.querySelector('[name="id"]')
      if (inp && inp.value && /^\d+$/.test(String(inp.value).trim())) return String(inp.value).trim()
    }
    return null
  }

  // Section ids the theme's cart drawer needs re-rendered on add, read live so
  // this stays theme-agnostic (Horizon nests the drawer in the header section).
  function cartSectionIds() {
    const ids = new Set()
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el.dataset.sectionId) ids.add(el.dataset.sectionId)
    })
    return [...ids].slice(0, 5)
  }


  const El = class extends HTMLElement {
    connectedCallback() {
      if (this._init) return
      this._init = true
      this._track = () => {}
      // both products selected by default; at least one must stay selected
      this._sel = { main: true, pair: true }
    }
    setAnalytics(t) { this._track = typeof t === 'function' ? t : () => {} }

    applyVariant(_content, pool) {
      this._pool = pool && typeof pool === 'object' ? pool : {}
      if (!this._pool.main || !this._pool.pair) return
      if (!this._sel) this._sel = { main: true, pair: true }
      this._render()
      this._bind()
    }

    _props() { return (this._pool && this._pool.props) || {} }
    _selectedKeys() { return ['main', 'pair'].filter((k) => this._sel[k]) }

    _bundlePrice() {
      const cfg = this._pool.config || {}
      const keys = this._selectedKeys()
      if (cfg.bundlePrice != null && keys.length === 2) return Number(cfg.bundlePrice)
      const minor = keys.reduce((s, k) => s + Number(this._pool[k].price || 0), 0)
      return minor / 100
    }

    _itemHtml(item, key) {
      const on = !!this._sel[key]
      const rating = (item.rating != null && item.rating !== '')
        ? `<div class="sai-fxsfrd01__rating"><span class="star">${starSvg()}</span><span class="val">${esc(fmtRating(item.rating))}</span>${item.rating_count ? `<span class="cnt">(${esc(String(item.rating_count))} +)</span>` : ''}</div>`
        : ''
      return `<div class="sai-fxsfrd01__item${on ? '' : ' is-off'}" data-card="${key}" role="checkbox" aria-checked="${on}" tabindex="0" aria-label="${on ? 'Remove' : 'Add'} ${esc(item.title)}">
        <div class="sai-fxsfrd01__imgbox"><span class="sai-fxsfrd01__check${on ? ' is-on' : ''}" aria-hidden="true">${checkSvg()}</span>${item.image ? `<img src="${esc(item.image)}" alt="${esc(item.title)}" loading="lazy" decoding="async">` : ''}</div>
        <div class="sai-fxsfrd01__info"><div class="sai-fxsfrd01__meta">${rating}<div class="sai-fxsfrd01__name">${esc(item.title)}</div></div></div>
      </div>`
    }

    _render() {
      const props = this._props()
      const cfg = this._pool.config || {}
      const cur = this._pool.currency
      const heading = cfg.heading || props.heading || 'Bundle & Pay Less'
      const n = this._selectedKeys().length
      const tpl = n >= 2
        ? (props.cta_template || 'Buy both at {price}')
        : (props.cta_template_single || 'Add to cart at {price}')
      const cta = tpl.replace('{price}', fmtMoney(this._bundlePrice(), cur))
      this.innerHTML = `<div class="sai-fxsfrd01__wrap">
        <p class="sai-fxsfrd01__heading">${esc(heading)}</p>
        <div class="sai-fxsfrd01__pair">${this._itemHtml(this._pool.main, 'main')}<span class="sai-fxsfrd01__plus">${plusSvg()}</span>${this._itemHtml(this._pool.pair, 'pair')}</div>
        <button type="button" class="sai-fxsfrd01__cta" data-action="buy-both"><span class="sai-fxsfrd01__cta-label">${esc(cta)}</span></button>
      </div>`
    }

    _toggle(which) {
      if (which !== 'main' && which !== 'pair') return
      const other = which === 'main' ? 'pair' : 'main'
      if (this._sel[which] && !this._sel[other]) return
      this._sel[which] = !this._sel[which]
      this._render()
    }

    _bind() {
      if (this._clickBound) return
      this._clickBound = true
      this.addEventListener('click', (e) => {
        const buy = e.target.closest('[data-action="buy-both"]')
        if (buy) { this._buyBoth(buy); return }
        const card = e.target.closest('[data-card]')
        if (card) { this._toggle(card.getAttribute('data-card')) }
      })
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return
        const card = e.target.closest('[data-card]')
        if (card) { e.preventDefault(); this._toggle(card.getAttribute('data-card')) }
      })
    }

    // Buy Both: add the anchor (this PDP's product, live-selected variant), then
    // hand off to the cross-sell drawer for the pair (its own Add To Cart / Skip).
    async _buyBoth(btn) {
      const keys = this._selectedKeys()
      if (!keys.length) return
      btn.classList.add('is-loading')
      btn.disabled = true
      try {
        if (keys.includes('main')) {
          const anchorId = liveAnchorVariantId() || (this._pool.main && this._pool.main.variant_id)
          if (anchorId) {
            const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: anchorId, quantity: 1 }] }) })
            if (!res.ok) throw new Error('anchor add failed: ' + res.status)
            this._track(`${FEATURE_SLUG}:buy_both`, {})
            this._track(`${FEATURE_SLUG}:added_to_cart`, { variant_id: anchorId, quantity: 1 })
          }
        }
        if (keys.includes('pair')) {
          // the cross-sell drawer renders the pair + its own Add To Cart / Skip
          document.dispatchEvent(new CustomEvent('frido:open-xsell-drawer'))
        } else {
          let cart = null
          try { const cr = await fetch('/cart.js', { headers: { Accept: 'application/json' } }); if (cr.ok) cart = await cr.json() } catch (_e) {}
          const evt = new Event('cart:update', { bubbles: true })
          evt.detail = { resource: cart || {}, sourceId: 'sai-fxsfrd01', data: { source: 'sai-fxsfrd01' } }
          document.dispatchEvent(evt)
        }
      } catch (err) {
        this._track(`${FEATURE_SLUG}:add_to_cart_error`, { error_message: String((err && err.message) || err).slice(0, 140) })
        console.error('[sai-fxsfrd01] buy both failed', err)
      }
      btn.classList.remove('is-loading')
      btn.disabled = false
    }
  }

  if (!customElements.get(TAG)) customElements.define(TAG, El)

  function bindAll() {
    const api = window.__spectrumAi && window.__spectrumAi.snippet
    for (const node of document.querySelectorAll(`[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`)) {
      const el = node.querySelector(TAG)
      if (!el) continue
      if (!el._painted) {
        const initPool = readPool(node)
        if (initPool && (initPool.product || initPool.bundle || initPool.axis || initPool.buying_options || initPool.main)) { el._painted = true; el.applyVariant({}, initPool) }
      }
      if (api && typeof api.bind === 'function' && !el._wrapBound) {
        el._wrapBound = true
        const handles = api.bind(node, (ctx) => {
          const e = node.querySelector(TAG)
          if (e) e.applyVariant(ctx.variant ? ctx.variant.content : {}, readPool(node))
        })
        if (handles) el.setAnalytics(handles.track)
      }
    }
    for (const el of document.querySelectorAll(TAG)) {
      if (el._applied || el._wrapBound || el.closest('[data-spectrum-instance-id]')) continue
      const parent = el.closest('[data-spectrum-lq-snippet]') || el.parentElement
      if (!parent) continue
      const pool = readPool(parent)
      if (pool && pool.main) { el._applied = true; el.applyVariant({}, pool) }
    }
  }

  function schedule() {
    bindAll()
    let tries = 0
    const t = setInterval(() => {
      bindAll()
      const api = window.__spectrumAi && window.__spectrumAi.snippet
      if ((api && typeof api.bind === 'function') || ++tries > 20) clearInterval(t)
    }, 500)
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true })
  } else {
    schedule()
  }

  const __saiRelevant = (records) => {
    for (const r of records) {
      for (const n of r.addedNodes) {
        if (n.nodeType !== 1) continue
        if ((n.matches && n.matches(`${TAG}, [data-spectrum-snippet-id="${SNIPPET_ID}"], [data-spectrum-lq-snippet]`)) ||
            (n.querySelector && n.querySelector(`${TAG}, [data-spectrum-snippet-id="${SNIPPET_ID}"]`))) return true
      }
    }
    return false
  }
  const __saiMo = new MutationObserver((records) => {
    if (__saiMo._t || !__saiRelevant(records)) return
    __saiMo._t = setTimeout(() => { __saiMo._t = null; bindAll() }, 150)
  })
  __saiMo.observe(document.documentElement, { childList: true, subtree: true })
  setTimeout(function () { try { __saiMo.disconnect() } catch (e) {} }, 30000)
})()
